import { FormListFieldData } from 'antd/lib/form';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import FormItem from 'antd/lib/form/FormItem';
import TextArea from 'antd/lib/input/TextArea';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { getIsAppleAppStore, validatePattern } from '../../helpers';
import {
    AppStoreType,
    GeneratedReleaseNotesMarkdownContext,
    GeneratedReleaseNotesStepContext,
    PatternToken,
} from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { CharCounter } from './CharCounter';
import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Divider from 'antd/lib/divider';
import { commonInputFieldStyle } from '@/components/StepContentMetadataCommon';
import { GenerateReleaseNotesDropdown } from '../sharedComponents/GenerateReleaseNotesDropdown';
import { PatternTokenHelperTextComponent } from '../appSettings/PatternTokenHelperTextComponent';
import Popover from 'antd/lib/popover';

interface ReleaseNotesInputProps {
    appId?: string;
    orgId?: string;
    releaseId?: string;
    fieldData: FormListFieldData;
    shouldShowTitle?: boolean;
    fieldDescription?: string;
    appStoreType: AppStoreType;
    saveDisabled: boolean;
    onClickSetDefaultReleaseNotes?: () => void;
    onClickSetTesterReleaseNotes?: () => void;
    onClickGetPreviousReleaseNotes?: () => void;
    isLoadingGetPreviousReleaseNotes?: boolean;
    shouldHideSetReleaseNotesDropdown?: boolean;
    onApplyChangesToAllLocales?: () => void;
    isApplyChangesToAllLocalesDisabled?: boolean;
    getPreviousReleaseNotesIsLoading?: boolean;
    onClickShowFeaturesList?: () => void;
    defaultValue?: string;
    displayLocale?: string;
    locale?: string;
    metadataFieldLocalizationStatusComponent?: React.ReactNode;
    allowsPatternedString?: boolean;
    availableTokens?: PatternToken[];
    disallowedTokens?: PatternToken[];
}

export const ReleaseNotesInput: React.FC<ReleaseNotesInputProps> = ({
    appId,
    orgId,
    releaseId,
    shouldShowTitle,
    fieldDescription,
    saveDisabled,
    appStoreType,
    onApplyChangesToAllLocales,
    onClickSetDefaultReleaseNotes,
    onClickGetPreviousReleaseNotes,
    isLoadingGetPreviousReleaseNotes,
    onClickSetTesterReleaseNotes,
    isApplyChangesToAllLocalesDisabled,
    shouldHideSetReleaseNotesDropdown,
    getPreviousReleaseNotesIsLoading,
    fieldData,
    displayLocale,
    locale,
    metadataFieldLocalizationStatusComponent,
    allowsPatternedString,
    availableTokens,
    disallowedTokens,
}) => {
    const characterLimit = getIsAppleAppStore(appStoreType) ? 4000 : 500;

    const fieldPath = [fieldData.name, 'whatsNew'];
    const fullFieldPath = ['localizations', ...fieldPath];

    return (
        <div style={{ display: 'flex', backgroundColor: antGrey.grey2, flexGrow: 1, gap: 24 }}>
            {shouldShowTitle && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                    }}
                >
                    <Text style={{ fontSize: 16, fontWeight: 600, marginBottom: 24 }}>{fieldDescription}</Text>
                </div>
            )}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'stretch',
                    flexDirection: 'column',
                    ...commonInputFieldStyle,
                }}
            >
                <div
                    style={{
                        marginBottom: 20,
                        display: 'flex',
                        gap: 10,
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            position: 'relative',
                        }}
                    >
                        <Space
                            style={{
                                position: 'absolute',
                                right: 0,
                                bottom: 'calc(100% + 12px)',
                            }}
                            size={12}
                        >
                            {metadataFieldLocalizationStatusComponent}
                            {orgId && appId && releaseId && (
                                <GenerateReleaseNotesDropdown
                                    orgId={orgId}
                                    appId={appId}
                                    releaseId={releaseId}
                                    stepContext={GeneratedReleaseNotesStepContext.Metadata}
                                    markdownContext={GeneratedReleaseNotesMarkdownContext.MarkdownUnsupported}
                                    locale={locale}
                                />
                            )}
                        </Space>
                        <FormItem
                            style={{ marginBottom: '0px' }}
                            name={fieldPath}
                            rules={[
                                {
                                    max: characterLimit,
                                    message: `Should be less than ${characterLimit} characters`,
                                },
                                { required: true, message: 'Please enter a release note' },
                                {
                                    validator: (_, value) => {
                                        if (allowsPatternedString !== true) {
                                            return Promise.resolve();
                                        }
                                        const pattern = value as string;

                                        return validatePattern(pattern, 'Release notes', [], disallowedTokens);
                                    },
                                },
                            ]}
                        >
                            <TextArea
                                disabled={saveDisabled}
                                placeholder="Add information about what's new in this release."
                                style={{ minWidth: 500, height: 250, position: 'relative' }}
                            />
                        </FormItem>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: !shouldHideSetReleaseNotesDropdown ? 'space-between' : 'flex-end',
                            alignItems: 'center',
                            gap: '20px',
                        }}
                    >
                        {!shouldHideSetReleaseNotesDropdown && (
                            <Text type="secondary">
                                Pattern token strings are supported. Token strings will be rendered immediately on save.{' '}
                                <Popover
                                    content={
                                        <div style={{ overflow: 'scroll', maxWidth: '40vw', maxHeight: '40vh' }}>
                                            <PatternTokenHelperTextComponent availableTokens={availableTokens} />
                                        </div>
                                    }
                                    placement="top"
                                >
                                    <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                </Popover>
                            </Text>
                        )}
                        <Space
                            size={2}
                            split={<Divider style={{ backgroundColor: antGrey.grey5, margin: 0 }} type="vertical" />}
                        >
                            {!shouldHideSetReleaseNotesDropdown && (
                                <Dropdown
                                    disabled={saveDisabled}
                                    menu={{
                                        items: [
                                            {
                                                label: 'From default release notes',
                                                key: '1',
                                                onClick: onClickSetDefaultReleaseNotes,
                                                disabled: !onClickSetDefaultReleaseNotes,
                                            },
                                            {
                                                label: 'From previous release',
                                                key: '2',
                                                onClick: onClickGetPreviousReleaseNotes,
                                                disabled:
                                                    !onClickGetPreviousReleaseNotes || getPreviousReleaseNotesIsLoading,
                                            },
                                            {
                                                label: 'From latest beta tester notes',
                                                key: '3',
                                                onClick: onClickSetTesterReleaseNotes,
                                                disabled: !onClickSetTesterReleaseNotes,
                                            },
                                        ],
                                    }}
                                >
                                    <Button
                                        loading={isLoadingGetPreviousReleaseNotes}
                                        type="link"
                                        disabled={saveDisabled}
                                    >
                                        <Space>
                                            <Text
                                                style={{
                                                    color: runwayColors.primary,
                                                }}
                                            >
                                                Populate release notes for {displayLocale}
                                            </Text>
                                            <DownOutlined />
                                        </Space>
                                    </Button>
                                </Dropdown>
                            )}
                            {onApplyChangesToAllLocales && (
                                <Button
                                    type="link"
                                    onClick={onApplyChangesToAllLocales}
                                    disabled={saveDisabled || isApplyChangesToAllLocalesDisabled}
                                >
                                    Copy this text to all locales
                                </Button>
                            )}
                        </Space>
                    </div>
                </div>
            </div>

            <div>
                <FormItem>
                    {(form) => {
                        const stringValue = form.getFieldValue(fullFieldPath);

                        return (
                            <Space direction="vertical">
                                <CharCounter stringValue={stringValue} maxLength={characterLimit} />
                            </Space>
                        );
                    }}
                </FormItem>
            </div>
        </div>
    );
};
